import { JsonDocumentInterface } from './document';
import { ResourceObject } from './document/ResourceObject';
import { Resource } from './Resource';
import { PageMeta } from './document/PageMeta';
import { MetaObject } from './document/MetaObject';
import { Typed } from './TypedAttributes';

export class JsonDocument<T> {
    private readonly document: JsonDocumentInterface<T>;

    public constructor(document: JsonDocumentInterface<T>) {
        this.document = document;
    }

    public getResources(): Array<Resource<T>> {
        if (this.document.data === null) {
            return [];
        }

        if (Array.isArray(this.document.data)) {
            const resourceArray: Resource<T>[] = [];
            this.document.data.forEach((resource: ResourceObject<T>) => {
                resourceArray.push(new Resource(resource, this));
            });

            return resourceArray;
        }

        return [new Resource<T>(this.document.data, this)];
    }

    public getResource(type: string, id: number | string): Resource<T> | undefined {
        const resources = this.getResources();

        return resources.find((r: Resource<T>) => r.getType() === type && r.getId() === String(id));
    }

    public getRelationsByType<U>(type: string): Array<Resource<U>> {
        return this.getResources().map((resource) => resource.getRelationsByType<U>(type))
            .reduce((flatRelations, currentRelations) => {
                currentRelations.forEach((currentRelation) => {
                    const index = flatRelations.findIndex(
                        (fr) => fr.getId() === currentRelation.getId() && fr.getType() === currentRelation.getType()
                    );

                    if (index === -1) {
                        flatRelations.push(currentRelation);
                    }
                });
                return flatRelations;
            }, []);
    }

    public getIncludes(): Array<ResourceObject<any>> {
        return this.document.included;
    }

    public getMeta(): PageMeta & MetaObject | undefined {
        return this.document.meta;
    }

    public getColumns(): Record<string, Typed> | Record<string, string> {
        const resources = this.getResources();

        if (!resources.length || !resources[0].getColumns()) {
            return {};
        }

        return resources[0].getColumns();
    }

    public isEmpty(): boolean {
        return !this.getResources().length;
    }

    public first(): Resource<T> | null {
        const resources = this.getResources();

        if (resources.length) {
            return resources[0];
        }

        return null;
    }
}
